"use client"

import { Confetti } from "@/app/components/confetti"
import { copyToClipboard } from "@/app/components/copy/copy"
import { XIcon } from "@/app/icons/x"
import { ItemMedia } from "@/app/item/view/item-media"
import { useDropPageHref } from "@/app/navigation/drop"
import { DropPageType } from "@/app/navigation/drop"
import { useProfilePageHref } from "@/app/navigation/profile"
import { useScanner } from "@/app/sdk/anaconda-sdk-scanner"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { UnionAddress } from "@anaconda/core/blockchain/address"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { MinimalDrop } from "@anaconda/core/drop/types"
import { Trans } from "@lingui/react/macro"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  useIsMobile,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import Link from "next/link"
import { useCallback, useState } from "react"

interface MintSuccessPopupProps {
  drop: MinimalDrop
  mintedCount: number
  transactionId: string
  open: boolean
  onClose: () => void
}

interface ContentProps {
  drop: MinimalDrop
  transactionId: string
  onClose: () => void
}

const MintSuccessContent = reatomComponent<ContentProps>(
  ({ drop, transactionId, onClose }) => {
    const scanner = useScanner(drop.blockchain)
    const { address: addressAtom } = useMolecule(WalletConnectMoleculeReatom)
    const address = addressAtom()
    const profileHref = useProfilePageHref(
      `${Blockchain.ETHEREUM}:${address}` as UnionAddress,
    )
    const [copied, setCopied] = useState(false)

    const transactionUrl = scanner.getTransactionLink(transactionId)

    const handleShare = useCallback(() => {
      const dropLink = window.location.href
      const shareText = encodeURIComponent(`Just minted "${drop.title}" -`)
      const shareUrl = encodeURIComponent(dropLink)
      const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`
      window.open(twitterUrl, "_blank", "width=600,height=400")
    }, [drop.title])

    const handleCopyLink = useCallback(async () => {
      const dropLink = window.location.href
      await copyToClipboard(dropLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }, [])

    return (
      <div className="flex flex-col items-center gap-6">
        <Confetti />

        <div className="flex flex-col items-center gap-4 pl-3">
          <div className="relative h-[120px] w-[120px] overflow-hidden rounded-[6px]">
            <ItemMedia
              src={drop.media.url}
              type={drop.media.type}
              alt={drop.title}
              className="h-full w-full object-cover"
            />
          </div>

          <h2 className="text-center font-headline text-[28px] font-medium leading-[36px] text-white">
            <Trans>"{drop.title}" is yours!</Trans>
          </h2>
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          <p className="text-center font-mono text-xs font-medium leading-4 tracking-[-0.24px] text-white">
            <Trans>Let's show-off a little</Trans>
          </p>

          <div className="flex items-center justify-center gap-6 mb-2">
            <button
              type="button"
              onClick={handleShare}
              className="flex flex-col items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-foreground/10 bg-transparent p-3">
                <XIcon className="h-6 w-6 text-white" />
              </div>
              <span className="font-mono text-xs font-medium leading-4 tracking-[-0.24px] text-foreground/40">
                x
              </span>
            </button>

            <button
              type="button"
              onClick={handleCopyLink}
              className="flex flex-col items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-foreground/10 bg-transparent p-3">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Copy link</title>
                  <path
                    d="M18.6683 5.33045C16.8135 3.47546 13.8061 3.47546 11.9513 5.33045L10.8907 6.39111C10.5978 6.68401 10.5978 7.15888 10.8907 7.45177C11.1836 7.74467 11.6584 7.74467 11.9513 7.45177L13.0119 6.39111C14.281 5.12191 16.3386 5.12191 17.6077 6.39111C18.8769 7.66032 18.8769 9.7181 17.6077 10.9873L15.4866 13.1086C14.2174 14.3778 12.1598 14.3778 10.8907 13.1086C10.5978 12.8157 10.123 12.8157 9.8301 13.1086C9.53723 13.4015 9.53723 13.8764 9.8301 14.1693C11.685 16.0243 14.6923 16.0243 16.5472 14.1693L18.6683 12.048C20.5232 10.193 20.5232 7.18544 18.6683 5.33045Z"
                    fill="white"
                  />
                  <path
                    d="M14.1688 9.83045C12.314 7.97546 9.30663 7.97546 7.45177 9.83045L5.3306 11.9518C3.47574 13.8068 3.47574 16.8143 5.3306 18.6693C7.18546 20.5243 10.1928 20.5243 12.0476 18.6693C12.3405 18.3764 12.3405 17.9015 12.0476 17.6086C11.7548 17.3157 11.2799 17.3157 10.9871 17.6086C9.71795 18.8778 7.6603 18.8778 6.39119 17.6086C5.12207 16.3394 5.12207 14.2816 6.39119 13.0124L8.51236 10.8911C9.78148 9.62191 11.8391 9.62191 13.1082 10.8911C13.4011 11.184 13.8759 11.184 14.1688 10.8911C14.4617 10.5982 14.4617 10.1233 14.1688 9.83045Z"
                    fill="white"
                  />
                </svg>
              </div>
              <span className="font-mono text-xs font-medium leading-4 tracking-[-0.24px] text-foreground/40">
                {copied ? <Trans>Copied!</Trans> : <Trans>Copy</Trans>}
              </span>
            </button>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-6">
          <Link href={profileHref} onClick={onClose} className="w-full">
            <button
              type="button"
              className="flex h-9 w-full items-center justify-center gap-1 rounded-[6px] bg-[#FAFF00] px-3 py-2 backdrop-blur-[10px] transition-opacity hover:opacity-90"
            >
              <span className="font-mono text-sm font-medium leading-5 text-[#0A0A0A]">
                <Trans>View in portfolio</Trans>
              </span>
            </button>
          </Link>

          <a
            href={transactionUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full text-center font-mono text-xs font-medium leading-4 tracking-[-0.24px] text-foreground/40 transition-colors hover:text-foreground/60"
          >
            <Trans>View Transaction</Trans>
          </a>
        </div>
      </div>
    )
  },
  "MintSuccessContent",
)

export const MintSuccessPopup = reatomComponent<MintSuccessPopupProps>(
  ({ drop, mintedCount, transactionId, open, onClose }) => {
    const isMobile = useIsMobile()

    const contentProps: ContentProps = {
      drop,
      transactionId,
      onClose,
    }

    if (isMobile) {
      return (
        <Sheet open={open} onOpenChange={onClose}>
          <SheetContent
            className="w-full gap-4 rounded-t-xl border border-foreground/10 bg-[rgba(10,10,10,0.9)] p-6 text-white backdrop-blur-[105px]"
            side="bottom"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{drop.title}</SheetTitle>
            </SheetHeader>
            <MintSuccessContent {...contentProps} />
          </SheetContent>
        </Sheet>
      )
    }

    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="w-[380px] max-w-none border border-foreground/10 bg-[rgba(10,10,10,0.9)] p-6 backdrop-blur-[105px] sm:rounded-xl">
          <DialogHeader className="sr-only">
            <DialogTitle>{drop.title}</DialogTitle>
          </DialogHeader>
          <MintSuccessContent {...contentProps} />
        </DialogContent>
      </Dialog>
    )
  },
  "MintSuccessPopup",
)
